/**
 * Button - Simplified button component with animation
 * Wraps the existing AnimateButton for consistent behavior
 */

import React from 'react'
import { Button as MuiButton, Link } from '@mui/material'
import AnimateButton from '../../@extended/AnimateButton'
import NextLink from 'next/link'

interface ButtonProps {
	children: React.ReactNode
	href?: string
	onClick?: () => void
	style?: 'primary' | 'secondary' | 'outlined'
	size?: 'small' | 'medium' | 'large'
	openInNewTab?: boolean
	disabled?: boolean
}

const Button: React.FC<ButtonProps> = ({
	children,
	href,
	onClick,
	style = 'primary',
	size = 'medium',
	openInNewTab = false,
	disabled = false
}) => {
	const getVariant = () => {
		switch (style) {
			case 'primary':
				return 'contained'
			case 'secondary':
				return 'text'
			case 'outlined':
				return 'outlined'
			default:
				return 'contained'
		}
	}

	const buttonElement = href ? (
		<MuiButton
			variant={getVariant()}
			size={size}
			disabled={disabled}
			color="primary"
			component={Link}
			target={openInNewTab ? '_blank' : undefined}
		>
			{children}
		</MuiButton>
	) : (
		<MuiButton variant={getVariant()} size={size} onClick={onClick} disabled={disabled} color="primary">
			{children}
		</MuiButton>
	)

	if (href) {
		return (
			<AnimateButton>
				<NextLink href={href} passHref target={openInNewTab ? '_blank' : undefined}>
					{buttonElement}
				</NextLink>
			</AnimateButton>
		)
	}

	return <AnimateButton>{buttonElement}</AnimateButton>
}

export default Button
